'use client';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import type { MotionProps } from 'motion/react';
import { AnimatePresence, LazyMotion, m } from 'motion/react';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@src/components/Button/Button';
import { Container } from '@src/components/Container/Container';
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink';
import { useTrackSelectNavCta } from '@src/utils/analytics.util';
import { useBreakpoints } from '@src/utils/breakpoints.util';
import { cn } from '@src/utils/tailwind';
import { defaultTopNavProps } from './TopNav.defaults';
import { type Menu, type TopNavProps } from './TopNav.props';
const loadFramerMotionFeatures = async () => await import('./framerMotionFeatures').then(res => res.framerMotionFeatures);
export const TopNavMobile = (props: TopNavProps = defaultTopNavProps) => {
  const [isExpanded, setExpanded] = useState(false);
  const [isSubExpanded, setSubExpanded] = useState(false);
  const [subMenu, setSubMenu] = useState<Menu | null>(null);
  const navBarRef = useRef<HTMLElement | null>(null);
  const trackSelectNavCta = useTrackSelectNavCta();
  const {
    isMobile,
    isTablet
  } = useBreakpoints();
  useEffect(() => {
    const body = document.querySelector('body');
    const html = document.querySelector('html');
    if (body && html && (isMobile || isTablet)) {
      body.style.overflow = isExpanded ? 'hidden' : 'auto';
      body.style.position = isExpanded ? 'relative' : 'unset';
      body.style.height = isExpanded ? '100%' : 'unset';
      html.style.overflow = isExpanded ? 'hidden' : 'auto';
      html.style.height = isExpanded ? '100%' : 'unset';
      return () => {
        body.style.overflow = 'auto';
        body.style.position = 'unset';
        body.style.height = 'unset';
        html.style.overflow = 'auto';
        html.style.height = 'unset';
      };
    }
  }, [isExpanded, isMobile, isTablet]);
  const renderFooter = () => {
    return <footer className="flex flex-col gap-3 border-t border-white/10 p-4 pt-8 text-bodi-gray-butters">
        {props?.mobileFooterLinks?.filter(item => Boolean(item.title))?.map(item_0 => <LocalizedLink onClick={() => {
        trackSelectNavCta({
          'cta url': item_0.href
        });
        setExpanded(false);
      }} key={item_0?.title} href={item_0.href ?? ''} rel={item_0.newTab ? 'noopener noreferrer' : ''} target={item_0.newTab ? '_blank' : '_self'}>
              {item_0.title}
            </LocalizedLink>)}
      </footer>;
  };
  const renderMenus = () => {
    const itemClasses = 'py-3 text-body-lg font-lateral font-medium';
    return <>
        <div className="flex flex-col gap-2 px-4 py-6">
          {props?.menus?.filter(menu => Boolean(menu.title))?.map((menu_0, index) => {
          if (!menu_0?.hasDropdown) {
            return <LocalizedLink onClick={() => {
              trackSelectNavCta({
                'cta url': menu_0.href
              });
              setExpanded(false);
            }} data-testid={`nav-menu-${index}`} key={menu_0?.title} className={itemClasses} href={menu_0.href ?? ''} rel={menu_0.newTab ? 'noopener noreferrer' : ''} target={menu_0.newTab ? '_blank' : '_self'}>
                    {menu_0.title}
                  </LocalizedLink>;
          }
          return <button data-testid={`nav-menu-${index}`} key={menu_0.title} className={cn(itemClasses, 'relative flex w-full cursor-pointer items-center gap-2 transition-all')} onClick={() => {
            setSubMenu(menu_0);
            setSubExpanded(true);
          }}>
                  {menu_0.title}
                  <ChevronRightIcon className="size-5 text-bodi-accent-bright-500" />
                </button>;
        })}
          <div className="fixed bottom-0 left-0 flex w-screen flex-col gap-3 border-t border-bodi-gray-dark bg-bodi-black-text px-4 py-5">
            <LocalizedLink onClick={() => {
            trackSelectNavCta({
              'cta url': props?.links?.logIn?.href
            });
          }} data-testid="logInBtn" href={props.links?.logIn?.href ?? ''} rel={props.links?.logIn?.newTab ? 'noopener noreferrer' : ''} target={props.links?.logIn?.newTab ? '_blank' : '_self'}>
              <Button className="w-full" variant="dark" size="medium">
                {props.links?.logIn?.title}
              </Button>
            </LocalizedLink>
            <LocalizedLink onClick={() => {
            trackSelectNavCta({
              'cta url': props?.links?.signUp?.href
            });
          }} data-testid="signUpBtn" href={props.links?.signUp?.href ?? ''} rel={props.links?.signUp?.newTab ? 'noopener noreferrer' : ''} target={props.links?.signUp?.newTab ? '_blank' : '_self'}>
              <Button className="w-full" variant="primary" size="medium">
                {props.links?.signUp?.title}
              </Button>
            </LocalizedLink>
          </div>
        </div>
        {renderFooter()}
      </>;
  };
  const renderSubMenu = () => {
    return <>
        <div className="relative flex h-14 items-center border-b border-white/10 p-4">
          <button className="size-8" onClick={() => {
          setSubExpanded(false);
        }}>
            <ChevronLeftIcon className="size-6 text-bodi-accent-bright-500" />
          </button>
          <p className="font-lateral text-body-lg">{subMenu?.title}</p>
        </div>
        <div className="flex flex-col gap-6 px-12 py-6 font-medium">
          {subMenu?.items?.filter(item_1 => Boolean(item_1.title))?.map((item_2, itemIdx) => <LocalizedLink data-testid={`sub-menu-${itemIdx}`} onClick={() => {
          setExpanded(false);
        }} href={item_2.href ?? ''} key={item_2.title} rel={item_2.newTab ? 'noopener noreferrer' : ''} target={item_2.newTab ? '_blank' : '_self'}>
                {item_2.title}
              </LocalizedLink>)}
          {subMenu?.hasFeaturedItemMobile && subMenu?.featuredItemMobile?.title && <div className="fixed bottom-0 left-0 w-screen border-t border-bodi-gray-dark bg-bodi-black-text px-4 py-5">
              <LocalizedLink data-testid="menu-featured-item-mobile" onClick={() => {
            trackSelectNavCta({
              'cta url': subMenu?.featuredItemMobile?.href
            });
            setExpanded(false);
          }} href={subMenu?.featuredItemMobile?.href ?? ''} rel={subMenu?.featuredItemMobile?.newTab ? 'noopener noreferrer' : ''} target={subMenu?.featuredItemMobile?.newTab ? '_blank' : '_self'} className="py-3 font-medium text-bodi-accent-bright-500">
                <Button className="w-full" size="medium">
                  {subMenu?.featuredItemMobile?.title}
                </Button>
              </LocalizedLink>
            </div>}
        </div>
      </>;
  };
  const renderHamburger = () => {
    return <button data-testid="topNavMobileMenu" onClick={() => {
      if (!isExpanded || !isSubExpanded) {
        setSubMenu(null);
        setSubExpanded(false);
      }
      setExpanded(!isExpanded);
    }} className={`flex size-6 appearance-none flex-col items-center justify-center
          *:h-0.5 *:w-6 *:rounded-sm *:bg-white *:transition-all`}>
        <div className={cn('-translate-y-1', {
        'rotate-45 translate-y-0.5': isExpanded
      })}></div>
        <div className={cn({
        'opacity-0': isExpanded
      })}></div>
        <div className={cn('translate-y-1', {
        '-rotate-45 -translate-y-0.5': isExpanded
      })}></div>
      </button>;
  };
  const renderPanels = () => {
    const transition = {
      type: 'spring',
      duration: 0.3,
      bounce: 0
    } satisfies MotionProps['transition'];
    const menuProps = {
      variants: {
        open: {
          x: '0'
        },
        closed: {
          x: '-100%'
        }
      },
      initial: 'closed',
      animate: isExpanded ? isSubExpanded ? 'closed' : 'open' : 'closed',
      exit: 'closed',
      transition
    } satisfies MotionProps;
    const subMenuProps = {
      variants: {
        closedRight: {
          x: '100%'
        },
        closedLeft: {
          x: '-100%'
        },
        open: {
          x: '0'
        }
      },
      initial: 'closedRight',
      animate: isSubExpanded ? isExpanded ? 'open' : 'closedLeft' : 'closedRight',
      exit: isExpanded ? 'closedRight' : 'closedLeft',
      transition
    } satisfies MotionProps;
    const asideClassNames = 'absolute z-50 overflow-y-scroll left-0 w-full bg-bodi-black-text bg-linear-to-t from-white/10 to-white/0 text-white';
    const navBox = navBarRef?.current?.getBoundingClientRect();
    const style = !navBox ? undefined : {
      top: `${navBox.height}px`,
      height: `calc(100dvh - ${navBox.top}px - ${navBox.height}px)`
    };
    return <LazyMotion features={loadFramerMotionFeatures}>
        <AnimatePresence>
          {isExpanded && <m.aside key="menu" className={asideClassNames} {...menuProps} style={style}>
              {renderMenus()}
            </m.aside>}

          {isSubExpanded && <m.aside key="submenu" className={asideClassNames} {...subMenuProps} style={style}>
              {renderSubMenu()}
            </m.aside>}
        </AnimatePresence>
      </LazyMotion>;
  };
  return <div className="relative overflow-x-clip lg:hidden">
      <nav ref={navBarRef} className="z-50 h-12 w-full bg-bodi-black-text font-medium text-bodi-white">
        <Container className="h-full *:h-full">
          <div className="flex h-full flex-row items-center justify-between">
            <div className="flex gap-6">
              <div className="flex items-center">{renderHamburger()}</div>
              {props?.logo?.src && <LocalizedLink onClick={() => {
              trackSelectNavCta({
                'cta url': props?.logo?.href
              });
            }} href={props?.logo?.href ?? ''}>
                  <Image src={props.logo.src} width={props?.logo?.mobileWidth} height={props?.logo?.mobileHeight} alt={props?.logo?.altText ?? ''} />
                </LocalizedLink>}
            </div>
            <div className="flex gap-6">
              {props?.links?.signUp && <LocalizedLink data-testid="signUpBtn" onClick={() => {
              trackSelectNavCta({
                'cta url': props?.links?.signUp?.href
              });
            }} href={props?.links?.signUp?.href ?? ''} rel={props.links?.signUp?.newTab ? 'noopener noreferrer' : ''} target={props.links?.signUp?.newTab ? '_blank' : '_self'} className="block">
                  <p className="text-body-md">{props?.links?.signUp?.title}</p>
                </LocalizedLink>}
            </div>
          </div>
        </Container>
      </nav>

      {renderPanels()}
    </div>;
};